import { Injectable } from '@angular/core';
import { BehaviorSubject, throwError } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { ERRORS, STORAGE_KEYS, Constants } from '../../models/constants';
import { CoreUtilityService } from '../../utilities/services/utility.service';

@Injectable({
  providedIn: 'root'
})
export class CoreSessionService {
  // TODO: Save data to localstorage as well
  private APP_SCHEMA = 'appSchema';
  private FULL_APP_SCHEMA = 'fullAppSchema';
  private LOGIN_STATUS = 'loginStatus';
  private CURR_APP_VISA_CAT = 'curr_appl_visa_cat';
  private CURR_APP_VISA_TYPE = 'curr_appl_visa_type';
  private REGISTERED_USER_TOKEN = 'user_token';
  private loggedInStatus = new BehaviorSubject<any>(false);
  private currentModuleObservable = new BehaviorSubject<any>([]);
  private hideAddApplicantObservable = new BehaviorSubject<any>(true);
  private isDeletedSubscriber = new BehaviorSubject<any>(false);
  // private currApplicantVisaTypeObservable = new BehaviorSubject<any>('');
  private APP_LANGUAGE_CODE = 'app_language_code';
  private APP_CULTURE_CODE = 'app_culture_code';
  private TENENT = 'tenent';
  private DOCUMENT_IDENTIFIER = 'document_identifier';
  private ROUTE_ID = 'route_id';
  private APPLICANT_APLHA_ID = 'applicant_alpha_id';
  private APPLICANT_GRP_APLHA_ID = 'applicant_grp_alpha_id';
  private LOGGED_IN_USING_EMAIL = 'logged_in_using_email';
  private LOGGED_IN_FROM_MODULE = 'logged_in_from_module';
  private APPLICATION_STATUS = 'application_status';
  private CURRENCY_CODE = 'currency_code';

  isLoggedIn = this.loggedInStatus.asObservable();
  currentModule$ = this.currentModuleObservable.asObservable();
  hideaddApplicantBtn = this.hideAddApplicantObservable.asObservable();
  // currApplicantVisaType$ = this.currApplicantVisaTypeObservable.asObservable();
  private appSchemaSubscriber = new BehaviorSubject<any>(null);
  appSchema$ = this.appSchemaSubscriber.asObservable();
  isDeleted = this.isDeletedSubscriber.asObservable();
  private loginFlagObservable = new BehaviorSubject<any>(false);
  loginFlag = this.loginFlagObservable.asObservable();

  constructor(private titleService: Title, private coreUtilityService: CoreUtilityService) {
    const loginStatus: boolean = this.loggedInStatus.value || localStorage.getItem(this.LOGIN_STATUS);
    const currentApplicantVisaCat = this.currentApplicantVisaCat;
    this.updateLoginStatus(loginStatus);
    // this.updateLoginStatus(currApplicantVisaType);
    this.setCurrApplicantVisaCategory(currentApplicantVisaCat);
  }

  /**
   * Get value from session storage
   * @param key - key
   */
  getValue(key: string, isMandatory: boolean = true) {
    try {
      const value: any = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (value && value !== 'null') {
        if (
          this.coreUtilityService.strictCompare(key, STORAGE_KEYS.countryCode) ||
          this.coreUtilityService.strictCompare(key, STORAGE_KEYS.primaryGroupMFAKey)
        ) {
          return value;
        }
        return isNaN(value) ? value : Number(value);
      } else if (isMandatory) {
        throw new Error(`${ERRORS.KEY_NOT_FOUND_IN_STORAGE}: ${key}`);
      } else {
        return null;
      }
    } catch (error) {
      throw error;
    }
  }

  // setValue(key: string, value: any) { sessionStorage.setItem(key, value); }

  /**
   * This method will clear the session storage
   */
  clearSession() {
    sessionStorage.clear();
  }

  /**
   * This method will clear some of the keys from localstorage
   */
  clearLocalStorage() {
    localStorage.removeItem(STORAGE_KEYS.primaryEmailId);
    localStorage.removeItem(STORAGE_KEYS.primaryCountryCode);
    localStorage.removeItem(STORAGE_KEYS.primaryMobileNumber);
    localStorage.removeItem(STORAGE_KEYS.primaryNationalityId);
    localStorage.removeItem(STORAGE_KEYS.emailId);
    localStorage.removeItem(STORAGE_KEYS.countryCode);
    localStorage.removeItem(STORAGE_KEYS.mobileNumber);
    localStorage.removeItem(STORAGE_KEYS.nationalityId);
    localStorage.removeItem(STORAGE_KEYS.primaryVisaCatg);
    localStorage.removeItem(STORAGE_KEYS.primaryVisaType);
    localStorage.removeItem(this.REGISTERED_USER_TOKEN);
    localStorage.removeItem(this.APP_SCHEMA);
    localStorage.removeItem(STORAGE_KEYS.primaryGroupMFAKey);
    localStorage.removeItem(STORAGE_KEYS.primaryDestinationId);
    localStorage.removeItem(STORAGE_KEYS.primaryResidencyId);
    localStorage.removeItem(STORAGE_KEYS.residencyId);
    localStorage.removeItem(STORAGE_KEYS.destinationId);
    localStorage.removeItem(STORAGE_KEYS.residencyCode);
    localStorage.removeItem(STORAGE_KEYS.destinationCode);
    localStorage.removeItem(Constants.GET_PRODUCT_CONFIG_FLAG.internalLoginMechanism);
    localStorage.removeItem(Constants.GET_PRODUCT_CONFIG_FLAG.allowCopyTravel);
    localStorage.removeItem(Constants.GET_PRODUCT_CONFIG_FLAG.allowCopyAccommodation);
    localStorage.removeItem(Constants.GET_PRODUCT_CONFIG_FLAG.allowEvisaFormDownload);
    localStorage.removeItem(Constants.GET_PRODUCT_CONFIG_FLAG.allowVafFormDownload);
  }

  /**
   * This method will set title
   * @param newTitle - new value
   */
  public setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  /**
   * This method will save app schema in browser
   * @param data - schema data
   */
  async saveApplicationSchemaInBrowser(data: object) {
    // TODO: Encrypt this data before saving
    localStorage.setItem(this.APP_SCHEMA, JSON.stringify(data));
    this.appSchemaSubscriber.next(data);
    return true;
  }

  /**
   * Save full application schema to session
   */
  async saveFullApplicationSchemaInBrowser(data: object) {
    // TODO: Encrypt this data before saving
    sessionStorage.setItem(this.FULL_APP_SCHEMA, JSON.stringify(data));
    return true;
  }

  /**
   * Get app schema
   */
  getApplicationSchema() {
    const data = localStorage.getItem(this.APP_SCHEMA) || null;
    return data ? JSON.parse(data) : data;
  }

  /**
   * Get unfiltered application schema
   */
  getFullApplicationSchema() {
    const data = sessionStorage.getItem(this.FULL_APP_SCHEMA) || null;
    return data ? JSON.parse(data) : data;
  }

  /**
   * Get all module schema
   */
  getAllModulesSchema(): Array<any> {
    try {
      const data = localStorage.getItem(this.APP_SCHEMA) || null;
      return data ? JSON.parse(data).modules : data;
    } catch (error) {
      return null;
    }
  }

  /**
   * This method will update login status of the user in session storgae
   * @param status - status
   */
  updateLoginStatus(status: boolean) {
    localStorage.setItem(this.LOGIN_STATUS, String(status));
    this.loggedInStatus.next(status);
  }

  /**
   * Observable to update current module
   */
  updateCurrentModule(moduleObj: object) {
    this.currentModuleObservable.next(moduleObj);
  }

  /**
   * Getter function to get current module
   */
  get currentModule(): object {
    return this.currentModuleObservable.value;
  }

  /**
   * Observable to update login flag
   */
  updateLoginFlag(flag: boolean) {
    this.loginFlagObservable.next(flag);
  }

  /**
   * Observable to update add applicant show/hide
   */
  updateAddApplicantFlag(val) {
    this.hideAddApplicantObservable.next(val);
  }

  /**
   * Getter function to get flag for add applicant button
   */
  get addApplicantFlag() {
    return this.hideAddApplicantObservable.value;
  }

  /**
   * Observable to update delete applicant flag
   */
  updateDeletedFlag(val) {
    this.isDeletedSubscriber.next(val);
  }

  /**
   * Getter function to get flag for delete applicant
   */
  get addDeletedFlag() {
    return this.isDeletedSubscriber.value;
  }

  /**
   * Get user logge in status
   */
  getLoggedInStatus() {
    return this.loggedInStatus.value;
  }

  /**
   * Getter function to get login status
   */
  get loginStatus() {
    return typeof this.loggedInStatus.value !== 'boolean'
      ? Boolean(JSON.parse(this.loggedInStatus.value))
      : Boolean(this.loggedInStatus.value);
  }

  /**
   * Getter function to get app schema
   */
  get containsAppSchema() {
    return this.getApplicationSchema() ? true : false;
  }

  /**
   * This method will set visa category of current app
   * @param visaCategory - visa category
   */
  setCurrApplicantVisaCategory(visaCategory: string) {
    sessionStorage.setItem(this.CURR_APP_VISA_CAT, String(visaCategory));
  }

  /**
   * This method will set visa type of current app
   * @param visaType - visa type
   */
  setCurrApplicantVisaType(visaType: string) {
    sessionStorage.setItem(this.CURR_APP_VISA_TYPE, String(visaType));
  }

  get currentApplicantVisaCat() {
    return sessionStorage.getItem(this.CURR_APP_VISA_CAT);
  }

  get currentApplicantVisaType() {
    return sessionStorage.getItem(this.CURR_APP_VISA_TYPE);
  }

  // TODO: Check If can we use same method for storing token after user logs in, now this is only for Registration
  /**
   * This method will update token in session storgae
   * @param token - token
   */
  updateTokenOnRegistration(token) {
    localStorage.setItem(this.REGISTERED_USER_TOKEN, token);
  }

  set appLanguageCode(lang: string) {
    localStorage.setItem(this.APP_LANGUAGE_CODE, lang);
  }

  get appLanguageCode() {
    return localStorage.getItem(this.APP_LANGUAGE_CODE) || null;
  }

  set appCultureCode(cultureCode: string) {
    localStorage.setItem(this.APP_CULTURE_CODE, cultureCode);
  }

  get appCultureCode() {
    return localStorage.getItem(this.APP_CULTURE_CODE) || null;
  }

  set tenent(mission: string) {
    localStorage.setItem(this.TENENT, mission);
  }

  get tenent() {
    return localStorage.getItem(this.TENENT) || null;
  }

  set documentIdentifierId(documentIdentifierId: string) {
    sessionStorage.setItem(this.DOCUMENT_IDENTIFIER, documentIdentifierId);
  }

  get documentIdentifierId() {
    return sessionStorage.getItem(this.DOCUMENT_IDENTIFIER) || null;
  }

  /**
   * get user token from session storage
   */
  getUserToken() {
    return localStorage.getItem('user_token');
  }

  /**
   * set route id to session storage
   * @param routeId - route id
   */
  setRouteId(routeId: number) {
    sessionStorage.setItem(this.ROUTE_ID, String(routeId));
  }

  get routeId(): number {
    const routeId = sessionStorage.getItem(this.ROUTE_ID) || null;
    return routeId ? Number(routeId) : null;
  }

  /**
   * set app id to session storage
   * @param id - id
   */
  setApplicantAplhaId(id: string) {
    sessionStorage.setItem(this.APPLICANT_APLHA_ID, id);
  }

  get applicantAplhaId() {
    return sessionStorage.getItem(this.APPLICANT_APLHA_ID) || null;
  }

  /**
   * set app group id to session storage
   * @param id - id
   */
  setApplicantGroupAplhaId(id: string) {
    sessionStorage.setItem(this.APPLICANT_GRP_APLHA_ID, id);
  }

  get applicantGroupAplhaId() {
    return sessionStorage.getItem(this.APPLICANT_GRP_APLHA_ID) || null;
  }

  /**
   * set currency code to session storgae
   * @param value - value
   */
  setCurrencyCode(value: string) {
    sessionStorage.setItem(this.CURRENCY_CODE, value);
  }

  get currencyCode() {
    return sessionStorage.getItem(this.CURRENCY_CODE);
  }

  set loggedInUsingEmail(loggedInUsingEmail: boolean) {
    sessionStorage.setItem(this.LOGGED_IN_USING_EMAIL, String(loggedInUsingEmail));
  }

  get loggedInUsingEmail() {
    return JSON.parse(sessionStorage.getItem('logged_in_using_email')) || false;
  }

  set loggedInFromModule(loggedInFromModule: string) {
    sessionStorage.setItem(this.LOGGED_IN_FROM_MODULE, loggedInFromModule);
  }

  get loggedInFromModule() {
    return sessionStorage.getItem(this.LOGGED_IN_FROM_MODULE) || '';
  }

  get applicationStatus(): string {
    return sessionStorage.getItem(this.APPLICATION_STATUS || '');
  }

  set applicationStatus(value: string) {
    sessionStorage.setItem(this.APPLICATION_STATUS, value);
  }

  /**
   * This method will set the key with value in local storage
   * @param key - localStorage key
   * @param value - localStorage key
   */
  setStorageValue(key: string, value: any) {
    localStorage.setItem(key, value);
  }

  /**
   * This method will get the value for specified key from local storage
   * @param key - localStorage key
   */
  getStorageValue(key: string) {
    return localStorage.getItem(key) || null;
  }

  /**
   * Set value to session
   * @param key - key to the value to be stored
   * @param value - value to be stored
   */
  saveToSession(key: string, value: any) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      throw error;
    }
  }
}
